import { useState } from 'react';
import type { Course, Player, Round } from '../lib/types';
import { matchStatus, paceStatus, scoreLabel, totalsFor } from '../lib/scoring';

interface Props {
  course: Course;
  round: Round;
  holeIndex: number;
  onHole: (i: number) => void;
  onScore: (playerId: string, hole: number, patch: Partial<{ strokes: number; putts: number; fairwayHit: boolean; gir: boolean; penalties: number }>) => void;
  onFinish: () => void;
}

function cellClass(strokes: number | null, par: number): string {
  if (strokes == null) return '';
  const d = strokes - par;
  if (d <= -2) return 'score-eagle';
  if (d === -1) return 'score-birdie';
  if (d === 1) return 'score-bogey';
  if (d >= 2) return 'score-worse';
  return '';
}

export default function ScoreScreen({ course, round, holeIndex, onHole, onScore, onFinish }: Props) {
  const hole = course.holes[holeIndex];
  const [active, setActive] = useState(round.players[0]?.id ?? '');
  const player = round.players.find((p) => p.id === active) ?? round.players[0];
  const card = round.scores[player?.id ?? '']?.find((s) => s.hole === hole.number);

  const holesComplete = course.holes.filter((h) =>
    round.players.every((p) => round.scores[p.id]?.find((s) => s.hole === h.number)?.strokes != null)
  ).length;
  const pace = paceStatus(round, holesComplete);

  const quick = [hole.par - 1, hole.par, hole.par + 1, hole.par + 2, hole.par + 3].filter((n) => n > 0);

  return (
    <div className="screen">
      <div className="card">
        <h2>HOLE {hole.number} · PAR {hole.par} · SI {hole.strokeIndex}</h2>

        <div className="row" style={{ marginBottom: 10 }}>
          {round.players.map((p) => {
            const s = round.scores[p.id]?.find((x) => x.hole === hole.number);
            return (
              <button
                key={p.id}
                className={`btn ${p.id === active ? 'primary' : ''}`}
                onClick={() => setActive(p.id)}
              >
                {p.name}
                {s?.strokes != null ? ` · ${s.strokes}` : ''}
              </button>
            );
          })}
        </div>

        <div className="numpad" style={{ marginBottom: 10 }}>
          {quick.map((n) => (
            <button
              key={n}
              className={`btn ${card?.strokes === n ? 'primary' : ''}`}
              onClick={() => onScore(player.id, hole.number, { strokes: n })}
            >
              {n}
              <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>
                {scoreLabel(n, hole.par)}
              </div>
            </button>
          ))}
        </div>

        <div className="numpad" style={{ marginBottom: 10 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].slice(0, 10).map((n) => (
            <button
              key={n}
              className={`btn ghost ${card?.strokes === n ? 'primary' : ''}`}
              onClick={() => onScore(player.id, hole.number, { strokes: n })}
            >
              {n}
            </button>
          ))}
        </div>

        <div className="row">
          <span className="muted" style={{ minWidth: 60 }}>Putts</span>
          {[0, 1, 2, 3, 4].map((n) => (
            <button
              key={n}
              className={`btn ${card?.putts === n ? 'primary' : 'ghost'}`}
              onClick={() => onScore(player.id, hole.number, { putts: n })}
            >
              {n}
            </button>
          ))}
          <div className="spacer" />
          {hole.par > 3 && (
            <button
              className={`btn ${card?.fairwayHit ? 'primary' : 'ghost'}`}
              onClick={() => onScore(player.id, hole.number, { fairwayHit: !card?.fairwayHit })}
            >
              Fairway
            </button>
          )}
          <button
            className={`btn ${card?.gir ? 'primary' : 'ghost'}`}
            onClick={() => onScore(player.id, hole.number, { gir: !card?.gir })}
          >
            GIR
          </button>
        </div>

        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn" onClick={() => onHole(holeIndex - 1)} disabled={holeIndex === 0}>
            ‹ Hole
          </button>
          <button
            className="btn primary"
            onClick={() => onHole(holeIndex + 1)}
            disabled={holeIndex >= course.holes.length - 1}
          >
            Next hole ›
          </button>
          <div className="spacer" />
          <span className={`pill ${pace.state === 'behind' ? 'bad' : pace.state === 'ahead' ? 'ok' : ''}`}>
            PACE {pace.deltaMin > 0 ? `+${pace.deltaMin}` : pace.deltaMin} min · {holesComplete} done
          </span>
          <button className="btn danger" onClick={onFinish}>
            Finish round
          </button>
        </div>
      </div>

      <div className="card">
        <h2>CARD · {round.format.toUpperCase()}</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="grid">
            <thead>
              <tr>
                <th className="name">Player</th>
                {course.holes.map((h) => (
                  <th key={h.number}>{h.number}</th>
                ))}
                <th>GROSS</th>
                <th>NET</th>
                <th>PTS</th>
              </tr>
              <tr>
                <th className="name">Par</th>
                {course.holes.map((h) => (
                  <th key={h.number}>{h.par}</th>
                ))}
                <th>{course.holes.reduce((s, h) => s + h.par, 0)}</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {round.players.map((p) => {
                const t = totalsFor(round, course, p);
                return (
                  <tr key={p.id}>
                    <td className="name">
                      {p.name} <span className="muted">({p.handicap})</span>
                    </td>
                    {course.holes.map((h, i) => {
                      const s = round.scores[p.id]?.find((x) => x.hole === h.number);
                      return (
                        <td
                          key={h.number}
                          className={`${i === holeIndex ? 'cur ' : ''}${cellClass(s?.strokes ?? null, h.par)}`}
                          onClick={() => {
                            setActive(p.id);
                            onHole(i);
                          }}
                        >
                          {s?.strokes ?? '·'}
                        </td>
                      );
                    })}
                    <td>{t.gross || '·'}</td>
                    <td>{t.net || '·'}</td>
                    <td>{t.points || '·'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {round.format === 'match' && round.players.length >= 2 && (
        <MatchCard course={course} round={round} a={round.players[0]} b={round.players[1]} />
      )}

      <div className="card">
        <h2>STATS</h2>
        <table className="grid">
          <thead>
            <tr>
              <th className="name">Player</th>
              <th>Holes</th>
              <th>To par</th>
              <th>Putts</th>
              <th>Fairways</th>
              <th>GIR</th>
            </tr>
          </thead>
          <tbody>
            {round.players.map((p) => {
              const t = totalsFor(round, course, p);
              return (
                <tr key={p.id}>
                  <td className="name">{p.name}</td>
                  <td>{t.holesPlayed}</td>
                  <td>{t.toPar > 0 ? `+${t.toPar}` : t.toPar}</td>
                  <td>{t.putts}</td>
                  <td>
                    {t.fairways.hit}/{t.fairways.of}
                  </td>
                  <td>
                    {t.gir.hit}/{t.gir.of}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatchCard({ course, round, a, b }: { course: Course; round: Round; a: Player; b: Player }) {
  const m = matchStatus(round, course, a, b);
  const text =
    m.up === 0
      ? 'All square'
      : `${m.up > 0 ? a.name : b.name} ${Math.abs(m.up)} up${m.dormie ? ' (dormie)' : ''}`;
  return (
    <div className="card">
      <h2>MATCH</h2>
      <div style={{ fontSize: 26, fontWeight: 800 }}>{text}</div>
      <div className="muted">
        {m.played} played · {m.remaining} to play {m.decided ? '· match decided' : ''}
      </div>
    </div>
  );
}
